import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';

// Messaging centre (admin): delivery log & errors, message wording, monthly email reports, settings.

const panel = { background: 'rgba(15,23,42,0.92)', borderRadius: 16, border: '1px solid rgba(56,189,248,0.15)', padding: 24 };
const input = { background: 'rgba(255,255,255,0.05)', border: '1.5px solid rgba(56,189,248,0.25)', borderRadius: 10, padding: '9px 12px', color: '#e2e8f0', fontSize: 14.5, fontFamily: 'inherit', colorScheme: 'dark', boxSizing: 'border-box' };
const label = { display: 'block', color: '#e2e8f0', fontSize: 12.5, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 6, fontWeight: 600 };
const btn = (color = '#38bdf8', solid = false) => ({
  background: solid ? 'linear-gradient(135deg, #0891b2, #0e7490)' : `${color}1a`, border: solid ? 'none' : `1px solid ${color}55`,
  borderRadius: 8, padding: '8px 14px', color: solid ? '#fff' : color, fontSize: 14.5, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
});
const th = { padding: '10px 12px', textAlign: 'left', color: '#cbd5e1', fontSize: 13, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700, whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', color: '#e2e8f0', fontSize: 14.5, verticalAlign: 'top' };
const opt = { background: '#0f172a' };
const CHANNEL = { sms: 'SMS', whatsapp: 'WhatsApp', email: 'Email' };
const KIND = { token: 'Token', payment_failed: 'Payment failed' };
const FAILED = ['failed', 'undelivered'];

const StatusBadge = ({ status, cleared }) => {
  const failed = FAILED.includes(status);
  const color = failed ? (cleared ? '#cbd5e1' : '#f87171') : status === 'queued' || status === 'sent' ? '#38bdf8' : '#10b981';
  return (
    <span style={{ background: `${color}1a`, border: `1px solid ${color}55`, borderRadius: 6, padding: '2px 8px', color, fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
      {failed ? '' : '✓ '}{status}{cleared ? ' · cleared' : ''}
    </span>
  );
};

const Notice = ({ n, onClose }) => (n ? (
  <div style={{ background: n.ok ? 'rgba(16,185,129,0.1)' : 'rgba(248,113,113,0.1)', border: `1px solid ${n.ok ? 'rgba(16,185,129,0.3)' : 'rgba(248,113,113,0.3)'}`, borderRadius: 10, padding: '10px 14px', color: n.ok ? '#10b981' : '#f87171', fontSize: 14.5, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
    <span style={{ wordBreak: 'break-word' }}>{n.text}</span>
    <button onClick={onClose} aria-label="Dismiss" style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>✕</button>
  </div>
) : null);

// ───────────────────────── Delivery log ─────────────────────────
function DeliveryTab({ headers, summary, reloadSummary }) {
  const [filters, setFilters] = useState({ channel: '', status: 'failed', q: '', cleared: false });
  const [data, setData] = useState({ messages: [], total: 0 });
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(null);
  const [resendTo, setResendTo] = useState({});
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);
  const PAGE = 50;

  const load = () => axios.get('/api/messaging/log', {
    headers,
    params: {
      channel: filters.channel || undefined, status: filters.status || undefined, q: filters.q || undefined,
      cleared: filters.cleared ? 'include' : undefined, limit: PAGE, offset: page * PAGE,
    },
  }).then((r) => { setData(r.data); setSelected([]); }).catch(() => {});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters.channel, filters.status, filters.cleared, page]);

  const clear = async (body, what) => {
    if (!window.confirm(`Clear ${what}? They will be hidden from the error list but kept in history.`)) return;
    setBusy('clear');
    try {
      const r = await axios.post('/api/messaging/log/clear', body, { headers });
      setNotice({ ok: true, text: `Cleared ${r.data.cleared} message${r.data.cleared === 1 ? '' : 's'}` });
      load(); reloadSummary();
    } catch (e) { setNotice({ ok: false, text: e.response?.data?.message || 'Clear failed' }); }
    finally { setBusy(''); }
  };

  const resend = async (m) => {
    setBusy(`resend:${m.id}`);
    try {
      const r = await axios.post(`/api/messaging/log/${m.id}/resend`, { phone: resendTo[m.id] || undefined }, { headers });
      setNotice({ ok: true, text: `${CHANNEL[m.channel]} re-sent to ${r.data.notification?.recipient}` });
    } catch (e) {
      setNotice({ ok: false, text: e.response?.data?.message || 'Re-send failed' });
    } finally { setBusy(''); load(); reloadSummary(); }
  };

  const restore = async (m) => {
    await axios.post(`/api/messaging/log/${m.id}/restore`, {}, { headers });
    load(); reloadSummary();
  };

  const failedVisible = data.messages.filter((m) => FAILED.includes(m.status) && !m.cleared_at);
  const allChecked = failedVisible.length > 0 && failedVisible.every((m) => selected.includes(m.id));
  const pages = Math.ceil(data.total / PAGE);
  const openErrors = summary?.openErrors || {};

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      {/* Channel health */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 16 }}>
        {['sms', 'whatsapp', 'email'].map((ch) => {
          const c = summary?.channels?.[ch] || {};
          const s = summary?.last30Days?.find((x) => x.channel === ch) || { ok: 0, failed: 0 };
          const state = !c.configured ? ['#cbd5e1', 'Not set up'] : !c.enabled ? ['#fbbf24', 'Switched off'] : ['#10b981', c.sandbox ? 'On (sandbox)' : 'On'];
          return (
            <div key={ch} style={{ ...panel, padding: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ color: '#e2e8f0', fontWeight: 700 }}>{CHANNEL[ch]}</span>
                <span style={{ color: state[0], fontSize: 13.5, fontWeight: 600 }}>● {state[1]}</span>
              </div>
              {ch === 'email' ? (
                <div style={{ color: '#e2e8f0', fontSize: 14.5 }}>Monthly reports{c.from ? <> from <span style={{ color: '#cbd5e1' }}>{c.from}</span></> : ''}</div>
              ) : (
                <div style={{ display: 'flex', gap: 16, color: '#e2e8f0', fontSize: 14.5, flexWrap: 'wrap' }}>
                  <span><b style={{ color: '#e2e8f0' }}>{s.ok}</b> sent (30 days)</span>
                  <span><b style={{ color: s.failed ? '#f87171' : '#e2e8f0' }}>{s.failed}</b> failed</span>
                  {openErrors[ch] > 0 && <span style={{ color: '#f87171', fontWeight: 600 }}>{openErrors[ch]} to review</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Notice n={notice} onClose={() => setNotice(null)} />

      {/* Filters — one row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <select value={filters.channel} onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, channel: e.target.value })); }} style={input} aria-label="Channel">
          <option value="" style={opt}>All channels</option>
          <option value="sms" style={opt}>SMS</option>
          <option value="whatsapp" style={opt}>WhatsApp</option>
        </select>
        {[['failed', 'Errors'], ['ok', 'Delivered'], ['', 'All']].map(([v, l]) => (
          <button key={l} onClick={() => { setPage(0); setFilters((f) => ({ ...f, status: v })); }}
            style={{ ...btn(filters.status === v ? '#38bdf8' : '#cbd5e1'), background: filters.status === v ? 'rgba(56,189,248,0.18)' : 'transparent' }}>{l}</button>
        ))}
        <form onSubmit={(e) => { e.preventDefault(); setPage(0); load(); }} style={{ display: 'flex', gap: 6 }}>
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Phone, meter or M-Pesa ref" style={{ ...input, width: 220 }} />
          <button type="submit" style={btn()}>Search</button>
        </form>
        <label style={{ color: '#e2e8f0', fontSize: 14.5, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={filters.cleared} onChange={(e) => { setPage(0); setFilters((f) => ({ ...f, cleared: e.target.checked })); }} /> Show cleared
        </label>
        <div style={{ flex: 1 }} />
        {selected.length > 0 && (
          <button disabled={!!busy} onClick={() => clear({ ids: selected }, `${selected.length} selected error(s)`)} style={btn('#fbbf24')}>
            Clear selected ({selected.length})
          </button>
        )}
        <button disabled={!!busy} onClick={() => clear({ all: true, channel: filters.channel || undefined }, `all ${filters.channel ? `${filters.channel.toUpperCase()} ` : ''}errors`)} style={btn('#f87171')}>
          Clear all errors
        </button>
      </div>

      {/* Table */}
      <div style={{ ...panel, padding: 0, overflow: 'hidden' }}>
        {data.messages.length === 0 ? (
          <p style={{ color: '#cbd5e1', textAlign: 'center', padding: 40, margin: 0 }}>
            {filters.status === 'failed' ? 'No message errors to review' : 'No messages found'}
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(56,189,248,0.1)' }}>
                  <th style={th}>
                    <input type="checkbox" aria-label="Select all errors" checked={allChecked} disabled={!failedVisible.length}
                      onChange={() => setSelected(allChecked ? [] : failedVisible.map((m) => m.id))} />
                  </th>
                  <th style={th}>Time</th><th style={th}>Channel</th><th style={th}>To</th><th style={th}>Payment</th>
                  <th style={th}>Status</th><th style={th}>Problem</th><th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.messages.map((m) => {
                  const failed = FAILED.includes(m.status);
                  return [
                    <tr key={m.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer' }} onClick={() => setOpen(open === m.id ? null : m.id)}>
                      <td style={td} onClick={(e) => e.stopPropagation()}>
                        {failed && !m.cleared_at && (
                          <input type="checkbox" aria-label="Select message" checked={selected.includes(m.id)}
                            onChange={() => setSelected((s) => (s.includes(m.id) ? s.filter((x) => x !== m.id) : [...s, m.id]))} />
                        )}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{new Date(m.created_at).toLocaleString('en-KE', { dateStyle: 'short', timeStyle: 'short' })}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: '#cbd5e1' }}>
                        {CHANNEL[m.channel]}
                        <div style={{ color: '#cbd5e1', fontSize: 12.5 }}>{KIND[m.kind] || m.kind}{m.resent_from ? ' · re-send' : ''}</div>
                      </td>
                      <td style={{ ...td, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{m.recipient}</td>
                      <td style={td}>
                        {m.meter ? <>Meter <span style={{ fontFamily: 'monospace' }}>{m.meter}</span></> : '—'}
                        <div style={{ color: '#cbd5e1', fontSize: 12.5 }}>{[m.mpesa_ref, m.amount && `KES ${Number(m.amount).toLocaleString()}`, m.tenant].filter(Boolean).join(' · ')}</div>
                      </td>
                      <td style={td}><StatusBadge status={m.status} cleared={!!m.cleared_at} /></td>
                      <td style={{ ...td, color: failed ? '#fca5a5' : '#cbd5e1', maxWidth: 320 }}>
                        {failed ? <>{m.error_code && <b>{m.error_code} </b>}{m.error_message}</> : '—'}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {failed && !m.cleared_at && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <input value={resendTo[m.id] ?? ''} onChange={(e) => setResendTo((r) => ({ ...r, [m.id]: e.target.value }))}
                              placeholder="New number (optional)" aria-label="Corrected phone number"
                              style={{ ...input, padding: '5px 8px', width: 160, fontSize: 13.5 }} />
                            <button disabled={!!busy} onClick={() => resend(m)} style={btn('#10b981')}>{busy === `resend:${m.id}` ? 'Sending…' : 'Resend'}</button>
                            <button disabled={!!busy} onClick={() => clear({ ids: [m.id] }, 'this error')} style={btn('#cbd5e1')}>Clear</button>
                          </div>
                        )}
                        {failed && m.cleared_at && <button onClick={() => restore(m)} style={btn('#cbd5e1')}>Restore</button>}
                      </td>
                    </tr>,
                    open === m.id && (
                      <tr key={`${m.id}-body`}>
                        <td colSpan={8} style={{ ...td, background: 'rgba(0,0,0,0.2)' }}>
                          <div style={{ color: '#cbd5e1', fontSize: 12.5, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Message text</div>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', color: '#cbd5e1' }}>{m.body || '(not recorded for older messages)'}</pre>
                          {m.cleared_at && (
                            <div style={{ color: '#cbd5e1', fontSize: 13.5, marginTop: 8 }}>
                              Cleared {new Date(m.cleared_at).toLocaleString('en-KE')}{m.cleared_by_name ? ` by ${m.cleared_by_name}` : ''}
                            </div>
                          )}
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center', padding: 12, color: '#cbd5e1', fontSize: 14.5 }}>
            <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} style={btn('#cbd5e1')}>‹</button>
            Page {page + 1} of {pages}
            <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} style={btn('#cbd5e1')}>›</button>
          </div>
        )}
      </div>

      <p style={{ color: '#cbd5e1', fontSize: 13.5, margin: 0, lineHeight: 1.6 }}>
        Common errors: <b>HTTP 401</b> wrong Africa’s Talking username or API key · <b>InsufficientBalance</b> top up the Africa’s Talking account ·
        <b> UserInBlacklist</b> the customer has blocked promotional SMS · <b>63003</b> (WhatsApp) the number isn’t on WhatsApp.
        Click a row to see the message that was sent.
      </p>
    </div>
  );
}

// ───────────────────────── Message text ─────────────────────────
const SAMPLE = { meter: '01904400', amount: '500', token: '1234 5678 9012 3456 7890', units: '2 m³', reference: 'MW-123', name: 'Jane', site: 'Nairobi Water', month: 'September 2026' };
const fill = (t) => String(t || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => SAMPLE[k] ?? '');

function TemplatesTab({ headers }) {
  const [templates, setTemplates] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);

  const load = () => axios.get('/api/messaging/templates', { headers }).then((r) => {
    setTemplates(r.data.templates);
    setDrafts(Object.fromEntries(r.data.templates.map((t) => [t.key, { body: t.body, subject: t.subject }])));
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const save = async (t) => {
    setBusy(t.key);
    try {
      await axios.put(`/api/messaging/templates/${t.key}`, drafts[t.key], { headers });
      setNotice({ ok: true, text: `Saved “${t.name}”` });
      load();
    } catch (e) { setNotice({ ok: false, text: e.response?.data?.message || 'Save failed' }); }
    finally { setBusy(''); }
  };
  const reset = async (t) => {
    if (!window.confirm(`Go back to the default wording for “${t.name}”?`)) return;
    await axios.delete(`/api/messaging/templates/${t.key}`, { headers });
    setNotice({ ok: true, text: 'Back to the default wording' });
    load();
  };
  const insert = (key, v) => setDrafts((d) => ({ ...d, [key]: { ...d[key], body: `${d[key].body}{{${v}}}` } }));

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <Notice n={notice} onClose={() => setNotice(null)} />
      {['sms', 'whatsapp', 'email'].map((g) => (
        <div key={g} style={{ display: 'grid', gap: 16 }}>
          <h3 style={{ color: '#ffffff', fontSize: 19, fontWeight: 700, margin: '8px 0 0' }}>{CHANNEL[g]}</h3>
          {g === 'whatsapp' && (
            <p style={{ color: '#cbd5e1', fontSize: 13.5, margin: 0, lineHeight: 1.6 }}>
              This wording is used in the Twilio sandbox, and when a customer has messaged you in the last 24 hours. Otherwise WhatsApp only allows
              Meta-approved templates. Add their Content SIDs under Settings, with the details in this order: {'{{1}}'} meter, {'{{2}}'} amount, {'{{3}}'} token, {'{{4}}'} units.
            </p>
          )}
          {templates.filter((t) => t.channel === g).map((t) => {
            const d = drafts[t.key] || { body: '' };
            const len = fill(d.body).length;
            const parts = len <= 160 ? 1 : Math.ceil(len / 153);
            const changed = d.body !== t.body || (d.subject ?? null) !== (t.subject ?? null);
            return (
              <div key={t.key} style={panel}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ color: '#e2e8f0', fontWeight: 700 }}>
                    {t.name}{t.customised && <span style={{ color: '#a78bfa', fontSize: 12.5, marginLeft: 8 }}>edited</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {t.variables.map((v) => (
                      <button key={v} onClick={() => insert(t.key, v)} title="Add to the end of the message"
                        style={{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.35)', borderRadius: 6, padding: '2px 8px', color: '#c4b5fd', fontSize: 12.5, cursor: 'pointer', fontFamily: 'monospace' }}>
                        {`{{${v}}}`}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 16 }}>
                  <div>
                    {t.channel === 'email' && (
                      <div style={{ marginBottom: 10 }}>
                        <label style={label}>Subject</label>
                        <input value={d.subject || ''} onChange={(e) => setDrafts((x) => ({ ...x, [t.key]: { ...d, subject: e.target.value } }))} style={{ ...input, width: '100%' }} />
                      </div>
                    )}
                    <label style={label}>{t.channel === 'email' ? 'Opening message' : 'Message'}</label>
                    <textarea rows={t.channel === 'email' ? 6 : 5} value={d.body}
                      onChange={(e) => setDrafts((x) => ({ ...x, [t.key]: { ...d, body: e.target.value } }))}
                      style={{ ...input, width: '100%', resize: 'vertical', lineHeight: 1.5 }} />
                    {t.channel === 'sms' && (
                      <div style={{ color: parts > 1 ? '#fbbf24' : '#cbd5e1', fontSize: 13.5, marginTop: 4 }}>
                        about {len} characters · {parts} SMS{parts > 1 ? ' per customer (costs more)' : ''}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={label}>Preview</label>
                    {t.channel === 'email' && <div style={{ color: '#e2e8f0', fontSize: 14.5, marginBottom: 6 }}><b>{fill(d.subject)}</b></div>}
                    <div style={{ background: t.channel === 'whatsapp' ? '#0b3d2e' : 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 12, color: '#e2e8f0', fontSize: 14.5, whiteSpace: 'pre-wrap', minHeight: 80 }}>
                      {fill(d.body)}
                      {t.channel === 'email' && <div style={{ color: '#cbd5e1', marginTop: 8 }}>[report tables follow, with a CSV attached]</div>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                  <button disabled={!changed || busy === t.key} onClick={() => save(t)} style={{ ...btn('#38bdf8', true), opacity: changed ? 1 : 0.5 }}>{busy === t.key ? 'Saving…' : 'Save'}</button>
                  {changed && <button onClick={() => setDrafts((x) => ({ ...x, [t.key]: { body: t.body, subject: t.subject } }))} style={btn('#cbd5e1')}>Undo changes</button>}
                  {t.customised && <button onClick={() => reset(t)} style={btn('#cbd5e1')}>Reset to default</button>}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ───────────────────────── Email reports ─────────────────────────
const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const thisMonth = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Nairobi', year: 'numeric', month: '2-digit' }).format(new Date()).slice(0, 7);
const monthName = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString('en-KE', { month: 'long', year: 'numeric' });

function ReportsTab({ headers, settings, summary }) {
  const [tenants, setTenants] = useState([]);
  const [month, setMonth] = useState(shiftMonth(thisMonth(), -1));
  const [target, setTarget] = useState('admin');
  const [preview, setPreview] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState(null);

  const loadRuns = () => axios.get('/api/messaging/reports/runs', { headers }).then((r) => setRuns(r.data.runs)).catch(() => {});
  useEffect(() => {
    axios.get('/api/tenants', { headers }).then((r) => setTenants(r.data.tenants.filter((t) => t.active))).catch(() => {});
    loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (target === 'all') { setPreview(null); return; }
    setBusy('preview');
    axios.get('/api/messaging/reports/preview', { headers, params: { month, tenant_id: /^\d+$/.test(target) ? target : undefined } })
      .then((r) => setPreview(r.data))
      .catch((e) => setNotice({ ok: false, text: e.response?.data?.message || 'Preview failed' }))
      .finally(() => setBusy(''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, target]);

  const send = async () => {
    const what = target === 'all' ? 'every tenant and the admin summary'
      : target === 'admin' ? 'the admin summary' : tenants.find((t) => String(t.id) === target)?.name;
    if (!window.confirm(`Email the ${monthName(month)} report for ${what} now?`)) return;
    setBusy('send');
    try {
      const r = await axios.post('/api/messaging/reports/send', { month, target }, { headers });
      setNotice({ ok: r.data.success, text: r.data.results.map((x) => `${x.label}: ${x.status}${x.message ? ` (${x.message})` : ''}`).join(' · ') });
    } catch (e) { setNotice({ ok: false, text: e.response?.data?.message || 'Sending failed' }); }
    finally { setBusy(''); loadRuns(); }
  };

  const months = Array.from({ length: 13 }, (_, i) => shiftMonth(thisMonth(), -i));
  const emailReady = summary?.channels?.email?.configured;

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div style={{ ...panel, padding: 18, color: '#e2e8f0', fontSize: 14.5, lineHeight: 1.6 }}>
        {!emailReady
          ? <>Email isn’t set up yet. Add SMTP_HOST, SMTP_USER, SMTP_PASS and EMAIL_FROM to the server’s .env, then restart the API.</>
          : settings.email_reports_enabled === 'true'
            ? <>Last month’s reports go out automatically on day <b style={{ color: '#e2e8f0' }}>{settings.report_day}</b> of each month at <b style={{ color: '#e2e8f0' }}>{String(settings.report_hour).padStart(2, '0')}:00</b> Nairobi time. Each tenant’s users get their site’s report, and admins get a summary of all sites.</>
            : <>⏸ Automatic monthly reports are switched off (see Settings). You can still send them by hand below.</>}
      </div>

      <Notice n={notice} onClose={() => setNotice(null)} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <div>
          <label style={label} htmlFor="rep-month">Month</label>
          <select id="rep-month" value={month} onChange={(e) => setMonth(e.target.value)} style={input}>
            {months.map((m) => <option key={m} value={m} style={opt}>{monthName(m)}{m === thisMonth() ? ' (so far)' : ''}</option>)}
          </select>
        </div>
        <div>
          <label style={label} htmlFor="rep-target">Report</label>
          <select id="rep-target" value={target} onChange={(e) => setTarget(e.target.value)} style={input}>
            <option value="admin" style={opt}>Admin summary (all sites)</option>
            {tenants.map((t) => <option key={t.id} value={String(t.id)} style={opt}>{t.name}</option>)}
            <option value="all" style={opt}>Everything (all tenants + admin)</option>
          </select>
        </div>
        <div style={{ flex: 1 }} />
        <button disabled={busy === 'send' || !emailReady} onClick={send} style={{ ...btn('#38bdf8', true), opacity: emailReady ? 1 : 0.5 }}>
          {busy === 'send' ? 'Sending…' : 'Send now'}
        </button>
      </div>

      {target !== 'all' && preview && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 16, alignItems: 'start' }}>
          <div style={{ ...panel, padding: 18 }}>
            <label style={label}>Subject</label>
            <div style={{ color: '#e2e8f0', fontSize: 14.5, marginBottom: 14 }}>{preview.subject}</div>
            <label style={label}>Goes to ({preview.recipients.length})</label>
            {preview.recipients.length === 0
              ? <div style={{ color: '#fbbf24', fontSize: 14.5 }}>No one with an email address. {target === 'admin' ? 'Add extra recipients under Settings.' : 'Assign users with email addresses to this tenant.'}</div>
              : preview.recipients.map((r) => <div key={r.email} style={{ color: '#cbd5e1', fontSize: 14.5 }}>{r.name || '—'} <span style={{ color: '#cbd5e1' }}>{r.email}</span></div>)}
            <div style={{ color: '#cbd5e1', fontSize: 13.5, marginTop: 14 }}>A CSV of the breakdown is attached to the email.</div>
          </div>
          <iframe title="Report preview" srcDoc={preview.html} sandbox=""
            style={{ gridColumn: '1 / -1', minWidth: 0, width: '100%', height: 640, border: '1px solid rgba(56,189,248,0.15)', borderRadius: 12, background: '#f1f5f9' }} />
        </div>
      )}
      {busy === 'preview' && <p style={{ color: '#cbd5e1', margin: 0 }}>Loading preview…</p>}

      <div style={{ ...panel, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(56,189,248,0.1)', color: '#e2e8f0', fontWeight: 600 }}>Recent sends</div>
        {runs.length === 0 ? <p style={{ color: '#cbd5e1', padding: 20, margin: 0 }}>No reports sent yet</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['When', 'Month', 'Report', 'Recipients', 'Status', 'Sent'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{new Date(r.created_at).toLocaleString('en-KE', { dateStyle: 'short', timeStyle: 'short' })}</td>
                    <td style={td}>{r.month}</td>
                    <td style={{ ...td, color: '#cbd5e1' }}>{r.scope === 'admin' ? 'Admin summary' : r.tenant}</td>
                    <td style={td}>{r.recipients || '—'}</td>
                    <td style={td}>
                      <span style={{ color: r.status === 'sent' ? '#10b981' : r.status === 'skipped' ? '#fbbf24' : '#f87171', fontWeight: 600 }}>{r.status}</span>
                      {r.error && <div style={{ color: '#cbd5e1', fontSize: 13.5 }}>{r.error}</div>}
                    </td>
                    <td style={td}>{r.triggered_by === 'schedule' ? 'Automatic' : 'By hand'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────── Enquiries ─────────────────────────
const ENQ_STATUS = { new: ['#38bdf8', 'New'], contacted: ['#fbbf24', 'Contacted'], closed: ['#cbd5e1', 'Closed'] };

function EnquiriesTab({ headers, onChange }) {
  const [status, setStatus] = useState('new');
  const [data, setData] = useState({ enquiries: [], counts: {} });
  const [notes, setNotes] = useState({});
  const [notice, setNotice] = useState(null);

  const load = () => axios.get('/api/messaging/enquiries', { headers, params: { status: status || undefined } })
    .then((r) => { setData(r.data); onChange && onChange(r.data.counts.new || 0); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const update = async (e, body) => {
    try {
      await axios.put(`/api/messaging/enquiries/${e.id}`, body, { headers });
      setNotice({ ok: true, text: `Updated enquiry from ${e.name}` });
      load();
    } catch (err) { setNotice({ ok: false, text: err.response?.data?.message || 'Update failed' }); }
  };

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Notice n={notice} onClose={() => setNotice(null)} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {[['new', 'New'], ['contacted', 'Contacted'], ['closed', 'Closed'], ['', 'All']].map(([v, l]) => (
          <button key={l} onClick={() => setStatus(v)}
            style={{ ...btn(status === v ? '#38bdf8' : '#cbd5e1'), background: status === v ? 'rgba(56,189,248,0.18)' : 'transparent' }}>
            {l}{v && data.counts[v] ? ` (${data.counts[v]})` : ''}
          </button>
        ))}
      </div>
      {data.enquiries.length === 0 && <div style={{ ...panel, color: '#cbd5e1', textAlign: 'center' }}>No enquiries here yet. They come from the “Talk to us” form on the home page.</div>}
      {data.enquiries.map((e) => {
        const [color, text] = ENQ_STATUS[e.status] || ENQ_STATUS.new;
        return (
          <div key={e.id} style={{ ...panel, padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
              <div>
                <div style={{ color: '#e2e8f0', fontWeight: 700, fontSize: 15 }}>{e.name}{e.organisation ? <span style={{ color: '#e2e8f0', fontWeight: 400 }}> · {e.organisation}</span> : ''}</div>
                <div style={{ color: '#cbd5e1', fontSize: 13.5 }}>{new Date(e.created_at).toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' })}{e.meters ? ` · ${e.meters} meters` : ''}</div>
              </div>
              <span style={{ color, background: `${color}1a`, border: `1px solid ${color}55`, borderRadius: 6, padding: '2px 10px', fontSize: 13.5, fontWeight: 600, height: 'fit-content' }}>{text}</span>
            </div>
            {e.message && <p style={{ color: '#cbd5e1', fontSize: 15, whiteSpace: 'pre-wrap', margin: '0 0 12px' }}>{e.message}</p>}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {e.phone && <a href={`tel:${e.phone.replace(/[^\d+]/g, '')}`} style={{ ...btn('#38bdf8'), textDecoration: 'none' }}>{e.phone}</a>}
              {e.phone && <a href={`https://wa.me/${e.phone.replace(/\D/g, '').replace(/^0/, '254')}`} target="_blank" rel="noreferrer" style={{ ...btn('#10b981'), textDecoration: 'none' }}>WhatsApp</a>}
              {e.email && <a href={`mailto:${e.email}?subject=${encodeURIComponent('Your enquiry to Majic Water')}`} style={{ ...btn('#a78bfa'), textDecoration: 'none' }}>{e.email}</a>}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <input value={notes[e.id] ?? e.notes ?? ''} onChange={(ev) => setNotes((n) => ({ ...n, [e.id]: ev.target.value }))}
                placeholder="Notes (e.g. called, sending a quote)" aria-label="Notes" style={{ ...input, flex: 1, minWidth: 220 }} />
              {(notes[e.id] ?? e.notes ?? '') !== (e.notes ?? '') && <button onClick={() => update(e, { notes: notes[e.id] })} style={btn('#38bdf8')}>Save note</button>}
              {e.status !== 'contacted' && <button onClick={() => update(e, { status: 'contacted' })} style={btn('#fbbf24')}>Mark contacted</button>}
              {e.status !== 'closed' && <button onClick={() => update(e, { status: 'closed' })} style={btn('#cbd5e1')}>Close</button>}
              {e.status === 'closed' && <button onClick={() => update(e, { status: 'new' })} style={btn('#38bdf8')}>Reopen</button>}
            </div>
            {e.handled_by_name && <div style={{ color: '#cbd5e1', fontSize: 13.5, marginTop: 8 }}>Last updated by {e.handled_by_name}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ───────────────────────── Settings ─────────────────────────
function SettingsTab({ headers, settings, setSettings, reloadSummary }) {
  const [form, setForm] = useState(settings);
  const [test, setTest] = useState({ channel: 'sms', to: '' });
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState('');
  useEffect(() => setForm(settings), [settings]);

  const save = async () => {
    setBusy('save');
    try {
      const r = await axios.put('/api/messaging/settings', form, { headers });
      setSettings(r.data.settings);
      reloadSummary();
      setNotice({ ok: true, text: 'Settings saved' });
    } catch (e) { setNotice({ ok: false, text: e.response?.data?.message || 'Save failed' }); }
    finally { setBusy(''); }
  };
  const sendTest = async () => {
    setBusy('test');
    try {
      const r = await axios.post('/api/messaging/test', test, { headers });
      setNotice({ ok: true, text: r.data.message });
    } catch (e) { setNotice({ ok: false, text: e.response?.data?.message || 'Test failed' }); }
    finally { setBusy(''); }
  };
  const toggle = (k, text, hint) => (
    <label style={{ display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer', marginBottom: 14 }}>
      <input type="checkbox" checked={form[k] === 'true'} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.checked ? 'true' : 'false' }))} style={{ marginTop: 3 }} />
      <span><span style={{ color: '#e2e8f0', fontSize: 15 }}>{text}</span><div style={{ color: '#cbd5e1', fontSize: 13.5 }}>{hint}</div></span>
    </label>
  );

  return (
    <div style={{ display: 'grid', gap: 20, maxWidth: 760 }}>
      <Notice n={notice} onClose={() => setNotice(null)} />
      <div style={panel}>
        <h3 style={{ color: '#ffffff', fontSize: 19, fontWeight: 700, margin: '0 0 16px' }}>Channels</h3>
        {toggle('sms_enabled', 'Send SMS to customers', 'Token and payment-failed messages through Africa’s Talking.')}
        {toggle('whatsapp_enabled', 'Send WhatsApp to customers', 'The same messages through Twilio, alongside the SMS.')}
        {toggle('email_reports_enabled', 'Email monthly reports automatically', 'Last month’s report to each tenant’s users and to admins.')}
      </div>
      <div style={panel}>
        <h3 style={{ color: '#ffffff', fontSize: 19, fontWeight: 700, margin: '0 0 16px' }}>Monthly reports</h3>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <label style={label} htmlFor="rep-day">Day of month</label>
            <input id="rep-day" type="number" min={1} max={28} value={form.report_day || ''} onChange={(e) => setForm((f) => ({ ...f, report_day: e.target.value }))} style={{ ...input, width: 110 }} />
          </div>
          <div>
            <label style={label} htmlFor="rep-hour">Hour (Nairobi)</label>
            <input id="rep-hour" type="number" min={0} max={23} value={form.report_hour || ''} onChange={(e) => setForm((f) => ({ ...f, report_hour: e.target.value }))} style={{ ...input, width: 110 }} />
          </div>
        </div>
        <label style={label} htmlFor="rep-extra">Extra recipients for the admin summary</label>
        <input id="rep-extra" value={form.report_extra_recipients || ''} onChange={(e) => setForm((f) => ({ ...f, report_extra_recipients: e.target.value }))}
          placeholder="finance@example.com, director@example.com" style={{ ...input, width: '100%' }} />
        <div style={{ color: '#cbd5e1', fontSize: 13.5, marginTop: 6 }}>All admin users get it automatically. Tenant reports go to each tenant’s assigned users.</div>
      </div>
      <div style={panel}>
        <h3 style={{ color: '#ffffff', fontSize: 19, fontWeight: 700, margin: '0 0 6px' }}>WhatsApp approved templates</h3>
        <div style={{ color: '#cbd5e1', fontSize: 13.5, marginBottom: 14 }}>Content SIDs (starting HX) from Twilio’s Content Template Builder. When set, they replace the values in the server’s .env.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 12 }}>
          <div>
            <label style={label} htmlFor="wa-tok">Token purchased</label>
            <input id="wa-tok" value={form.whatsapp_token_content_sid || ''} onChange={(e) => setForm((f) => ({ ...f, whatsapp_token_content_sid: e.target.value.trim() }))} placeholder="HX…" style={{ ...input, width: '100%', fontFamily: 'monospace' }} />
          </div>
          <div>
            <label style={label} htmlFor="wa-fail">Payment failed</label>
            <input id="wa-fail" value={form.whatsapp_failure_content_sid || ''} onChange={(e) => setForm((f) => ({ ...f, whatsapp_failure_content_sid: e.target.value.trim() }))} placeholder="HX…" style={{ ...input, width: '100%', fontFamily: 'monospace' }} />
          </div>
        </div>
      </div>
      <div style={panel}>
        <h3 style={{ color: '#ffffff', fontSize: 19, fontWeight: 700, margin: '0 0 6px' }}>Public home page</h3>
        <div style={{ color: '#cbd5e1', fontSize: 13.5, marginBottom: 14 }}>Contact buttons and the animation on smartwater.co.ke. Leave a field empty to hide that button.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 12, marginBottom: 12 }}>
          <div><label style={label} htmlFor="c-phone">Phone (Call us)</label><input id="c-phone" value={form.contact_phone || ''} onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))} placeholder="0712 345 678" style={{ ...input, width: '100%' }} /></div>
          <div><label style={label} htmlFor="c-wa">WhatsApp number</label><input id="c-wa" value={form.contact_whatsapp || ''} onChange={(e) => setForm((f) => ({ ...f, contact_whatsapp: e.target.value }))} placeholder="0712 345 678" style={{ ...input, width: '100%' }} /></div>
          <div><label style={label} htmlFor="c-email">Email</label><input id="c-email" value={form.contact_email || ''} onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value.trim() }))} placeholder="info@smartwater.co.ke" style={{ ...input, width: '100%' }} /></div>
        </div>
        <label style={label} htmlFor="c-media">Animation (video, GIF or YouTube/Vimeo link)</label>
        <input id="c-media" value={form.home_media_url || ''} onChange={(e) => setForm((f) => ({ ...f, home_media_url: e.target.value.trim() }))}
          placeholder="https://youtu.be/…  or  /media/how-it-works.mp4" style={{ ...input, width: '100%', marginBottom: 6 }} />
        <div style={{ color: '#cbd5e1', fontSize: 13.5, marginBottom: 12 }}>
          Short and silent works best (10–20 s, 16:10). Videos play muted on a loop. Put your own files in <code>client/public/media/</code> and use <code>/media/file.mp4</code>.
          Leave empty to show the built-in animation.
        </div>
        <label style={label} htmlFor="c-enq">Send new enquiries to</label>
        <input id="c-enq" value={form.enquiry_recipients || ''} onChange={(e) => setForm((f) => ({ ...f, enquiry_recipients: e.target.value }))}
          placeholder="sales@example.com (leave empty to email all admins)" style={{ ...input, width: '100%' }} />
      </div>
      <div><button onClick={save} disabled={busy === 'save'} style={btn('#38bdf8', true)}>{busy === 'save' ? 'Saving…' : 'Save settings'}</button></div>

      <div style={panel}>
        <h3 style={{ color: '#ffffff', fontSize: 19, fontWeight: 700, margin: '0 0 16px' }}>Send a test message</h3>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <select value={test.channel} onChange={(e) => setTest((t) => ({ ...t, channel: e.target.value }))} style={input} aria-label="Test channel">
            <option value="sms" style={opt}>SMS</option>
            <option value="whatsapp" style={opt}>WhatsApp</option>
            <option value="email" style={opt}>Email</option>
          </select>
          <input value={test.to} onChange={(e) => setTest((t) => ({ ...t, to: e.target.value }))} aria-label="Send to"
            placeholder={test.channel === 'email' ? 'name@example.com' : '0712 345 678'} style={{ ...input, flex: 1, minWidth: 200 }} />
          <button onClick={sendTest} disabled={busy === 'test' || !test.to} style={btn('#10b981')}>{busy === 'test' ? 'Sending…' : 'Send test'}</button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── Page ─────────────────────────
export default function MessagingPage() {
  const [tab, setTab] = useState('delivery');
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState({});
  const [newEnquiries, setNewEnquiries] = useState(0);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const reloadSummary = () => axios.get('/api/messaging/summary', { headers }).then((r) => setSummary(r.data)).catch((e) => {
    if (e.response?.status === 401) { localStorage.clear(); navigate('/login'); }
    if (e.response?.status === 403) navigate('/dashboard');
  });
  useEffect(() => {
    reloadSummary();
    axios.get('/api/messaging/settings', { headers }).then((r) => setSettings(r.data.settings)).catch(() => {});
    axios.get('/api/messaging/enquiries', { headers, params: { status: 'new' } }).then((r) => setNewEnquiries(r.data.counts.new || 0)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openErrors = Object.values(summary?.openErrors || {}).reduce((a, b) => a + b, 0);
  const tabs = [
    ['delivery', `Delivery & errors${openErrors ? ` (${openErrors})` : ''}`],
    ['templates', 'Message text'],
    ['reports', 'Email reports'],
    ['enquiries', `Enquiries${newEnquiries ? ` (${newEnquiries})` : ''}`],
    ['settings', 'Settings'],
  ];

  return (
    <div style={{ minHeight: '100vh' }}>
      <Navbar user={user} />
      <div className="mw-page" style={{ padding: '32px 24px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ color: '#0f172a', fontSize: 26, fontWeight: 700, margin: 0 }}>Messaging</h1>
          <p style={{ color: '#475569', fontSize: 14, marginTop: 6 }}>SMS, WhatsApp and email: delivery problems, message wording, monthly reports and website enquiries</p>
        </div>
        <div data-tour="messaging-tabs" role="tablist" style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#0f172a', borderRadius: 12, padding: 4, width: 'fit-content', maxWidth: '100%', flexWrap: 'wrap' }}>
          {tabs.map(([id, text]) => (
            <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
              style={{ background: tab === id ? 'rgba(74,155,224,0.25)' : 'none', border: 'none', borderRadius: 8, padding: '8px 16px', color: tab === id ? '#7cc4ff' : '#e2e8f0', fontSize: 14.5, fontWeight: tab === id ? 700 : 500, cursor: 'pointer' }}>
              {text}
            </button>
          ))}
        </div>
        {tab === 'delivery' && <DeliveryTab headers={headers} summary={summary} reloadSummary={reloadSummary} />}
        {tab === 'templates' && <TemplatesTab headers={headers} />}
        {tab === 'reports' && <ReportsTab headers={headers} settings={settings} summary={summary} />}
        {tab === 'enquiries' && <EnquiriesTab headers={headers} onChange={setNewEnquiries} />}
        {tab === 'settings' && <SettingsTab headers={headers} settings={settings} setSettings={setSettings} reloadSummary={reloadSummary} />}
      </div>
    </div>
  );
}
